import { useState } from 'react';
import { Send, ArrowRight, Phone, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { products, companyInfo, CATALOGUE_URL } from '../../data/mock';
import { useInView } from '../../hooks/useInView';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InquirySection = () => {
  const [ref, isInView] = useInView();
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    productRequired: '',
    quantity: '',
    specifications: '',
    location: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save to backend
      await axios.post(`${API}/inquiries`, {
        company_name: formData.companyName,
        contact_person: formData.contactPerson,
        phone: formData.phone,
        email: formData.email,
        product_required: formData.productRequired,
        quantity: formData.quantity,
        specifications: formData.specifications,
        location: formData.location,
      });

      // Also open mailto for direct email
      const subject = encodeURIComponent(`Inquiry for ${formData.productRequired || 'Industrial Products'} - ${formData.companyName}`);
      const body = encodeURIComponent(
        `Company Name: ${formData.companyName}\n` +
        `Contact Person: ${formData.contactPerson}\n` +
        `Phone: ${formData.phone}\n` +
        `Email: ${formData.email}\n` +
        `Product Required: ${formData.productRequired}\n` +
        `Quantity: ${formData.quantity}\n` +
        `Technical Specifications: ${formData.specifications}\n` +
        `Location: ${formData.location}\n`
      );
      window.location.href = `mailto:${companyInfo.emails[0]}?cc=${companyInfo.emails[1]}&subject=${subject}&body=${body}`;

      toast.success('Inquiry saved! Opening email client to send.');
      setFormData({
        companyName: '', contactPerson: '', phone: '', email: '',
        productRequired: '', quantity: '', specifications: '', location: '',
      });
    } catch (err) {
      // Fallback to mailto only
      const subject = encodeURIComponent(`Inquiry for ${formData.productRequired || 'Industrial Products'} - ${formData.companyName}`);
      const body = encodeURIComponent(
        `Company Name: ${formData.companyName}\nContact Person: ${formData.contactPerson}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nProduct: ${formData.productRequired}\nQuantity: ${formData.quantity}\nSpecs: ${formData.specifications}\nLocation: ${formData.location}\n`
      );
      window.location.href = `mailto:${companyInfo.emails[0]}?cc=${companyInfo.emails[1]}&subject=${subject}&body=${body}`;
      toast.success('Opening email client to send inquiry.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="inquiry" className="py-20 lg:py-28 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left Info */}
          <div className={`lg:col-span-2 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="section-divider" />
              <span className="text-sm font-semibold text-ind-red-600 uppercase tracking-wider">Get in Touch</span>
            </div>
            <h2 className="font-heading font-extrabold text-navy-950 text-3xl lg:text-4xl mb-4">
              Get Industrial Pricing Within 24 Hours
            </h2>
            <p className="text-navy-500 text-base mb-8">
              Send your BOQ for immediate pricing. Our technical team will review your requirements and provide a competitive quotation.
            </p>

            {/* Quick info cards */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 bg-navy-50/60 border border-navy-100 rounded-xl p-4">
                <div className="w-10 h-10 rounded-lg bg-ind-red-50 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-ind-red-600" />
                </div>
                <div>
                  <p className="font-heading font-bold text-navy-900 text-sm">Quick Response</p>
                  <p className="text-xs text-navy-500">Quotation within 24 working hours</p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-navy-50/60 border border-navy-100 rounded-xl p-4">
                <div className="w-10 h-10 rounded-lg bg-ind-red-50 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-ind-red-600" />
                </div>
                <div>
                  <p className="font-heading font-bold text-navy-900 text-sm">Call Directly</p>
                  <div className="flex flex-wrap gap-2">
                    {companyInfo.phones.map(phone => (
                      <a key={phone} href={`tel:${phone}`} className="text-xs text-ind-red-600 font-medium hover:underline">{phone}</a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className={`lg:col-span-3 ${isInView ? 'animate-slide-in-right' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
            <form onSubmit={handleSubmit} className="bg-navy-950 rounded-2xl p-6 lg:p-8 relative overflow-hidden">
              <div className="absolute inset-0 grid-pattern opacity-40" />
              <div className="relative">
                <h3 className="font-heading font-bold text-white text-xl mb-6">Send BOQ for Immediate Pricing</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs font-medium">Company Name *</Label>
                    <Input
                      required
                      value={formData.companyName}
                      onChange={(e) => handleChange('companyName', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg focus:border-ind-red-500 focus:ring-ind-red-500/20"
                      placeholder="Your company name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs font-medium">Contact Person *</Label>
                    <Input
                      required
                      value={formData.contactPerson}
                      onChange={(e) => handleChange('contactPerson', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg focus:border-ind-red-500 focus:ring-ind-red-500/20"
                      placeholder="Full name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs font-medium">Phone *</Label>
                    <Input
                      required
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg focus:border-ind-red-500 focus:ring-ind-red-500/20"
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs font-medium">Email *</Label>
                    <Input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg focus:border-ind-red-500 focus:ring-ind-red-500/20"
                      placeholder="email@company.com"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs font-medium">Product Required *</Label>
                    <Select value={formData.productRequired} onValueChange={(val) => handleChange('productRequired', val)}>
                      <SelectTrigger className="bg-navy-900/60 border-navy-700 text-white h-10 rounded-lg focus:border-ind-red-500 focus:ring-ind-red-500/20 [&>span]:text-navy-400">
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent className="bg-navy-900 border-navy-700">
                        {products.map((p) => (
                          <SelectItem key={p.slug} value={p.name} className="text-white hover:bg-navy-800 focus:bg-navy-800 focus:text-white">
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs font-medium">Quantity</Label>
                    <Input
                      value={formData.quantity}
                      onChange={(e) => handleChange('quantity', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg focus:border-ind-red-500 focus:ring-ind-red-500/20"
                      placeholder="e.g., 500 meters"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs font-medium">Technical Specifications</Label>
                    <Textarea
                      value={formData.specifications}
                      onChange={(e) => handleChange('specifications', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 rounded-lg focus:border-ind-red-500 focus:ring-ind-red-500/20 min-h-[80px]"
                      placeholder="Size, pressure rating, material..."
                      rows={3}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs font-medium">Location / Delivery City</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg focus:border-ind-red-500 focus:ring-ind-red-500/20"
                      placeholder="City, State"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-ind-red-600 hover:bg-ind-red-700 text-white font-bold h-12 rounded-lg text-base shadow-lg shadow-ind-red-600/25"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
